import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { FilterListComponent } from "@components/filter-list/filter-list.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { Observable, Subject } from "rxjs";
import {
    distinctUntilChanged,
    map,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Handelssortimentsgrupp } from "~app/common/models/constants";
import { HandelssortimentResponseDTO } from "~app/common/models/handelssortiment/HandelssortimentResponseDTO";
import { RavaruHandelssortimentDTO } from "~app/common/models/handelssortiment/RavaruHandelssortimentDTO";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IHandelssortimentProdukt, IHandelssortimentsgrupp, ISortiment } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { HandelssortimentDTO } from "../../../../../common/models/handelssortiment/HandelssortimentDTO";
import { HandelssortimentSortimentDTO } from "../../../../../common/models/handelssortiment/HandelssortimentSortimentDTO";
import { HandelssortimentService } from "../../handelssortiment.service";

@Component({
    selector: "rask-handelssortiment-sortiment",
    styleUrls: ["./sortiment.component.scss"],
    templateUrl: "./sortiment.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        TextmultilineComponent,
        DatepickerComponent,
        InputNumberComponent,
        ButtonComponent,
        FilterListComponent,
        RouterLink,
        TextareaComponent
    ]
})
export class SortimentComponent implements OnInit, OnChanges, OnDestroy {
    allSortimentSelection: ISortiment[] = undefined;
    choosenSortimentSelection: ISortiment[] = undefined;
    @Input() foradlingsHandelssortiment: RavaruHandelssortimentDTO[];
    foreslagnamattslagList: Observable<string[]>;
    @Input() form: UntypedFormGroup;
    @Input() handelssortiment: HandelssortimentDTO;
    @Output() handelssortimentChange = new EventEmitter();
    @Input() handelssortimentSortiment: HandelssortimentSortimentDTO;
    @Output() handelssortimentSortimentChange = new EventEmitter();
    handelssortimentgruppArray: IHandelssortimentsgrupp[];
    handelssortimentgruppList: Observable<IHandelssortimentsgrupp[]>;
    initialized = false;
    @Input() produkter: IHandelssortimentProdukt[];
    ravaruHandelssortiment: HandelssortimentResponseDTO = null;
    selectedSortiment: ISortiment[] = [];
    sortimentList: Observable<ISortiment[]>;
    @Input() validationContext: IValidationContext;
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly busyService = inject(BusyService);
    private readonly destroy$ = new Subject<void>();
    private readonly handelssortimentService = inject(HandelssortimentService);
    private readonly modalService = inject(RaskModalService);

    addSortiment() {
        this.selectedSortiment.push(...this.allSortimentSelection);
        this.allSortimentSelection = undefined;
        this.updateDto();
    }

    changeHuvudSortiment(hsid) {
        this.handelssortimentSortiment.huvudsortimentId = hsid;
        this.updateDto();
    }

    downloadFDI(viol3: boolean) {
        this.busyService.addWork(this.handelssortimentService.fetchFDIDokumentUrl(this.handelssortiment.id, viol3)).subscribe(url => {
            if (url) {
                window.open(url, "_blank");
            } else {
                let message: string;
                if (this.produkter?.length > 0) {
                    message = "FDI-fil håller på att skapas. Försök igen lite senare.";
                } else {
                    message = "FDI-fil saknas. Minst en produkt måste vara kopplad för att filen ska skapas.";
                }
                this.modalService.info(new ModalSettings("FDI-fil", message));
            }
        });
    }

    handelssortimentsgruppFormatter = (hg: IHandelssortimentsgrupp) => hg.namn;

    handelssortimentsgruppOutputFormatter = (hg: IHandelssortimentsgrupp) => hg ? hg.id : undefined;

    isHandelssortimentsgruppEnergived(): boolean {
        const hsgid = this.form.get("handelssortimentsgruppId").value as number;
        if (this.handelssortimentgruppArray && hsgid) {
            const grupp = this.handelssortimentgruppArray.find(x => x.id === hsgid);
            return (grupp?.namn === Handelssortimentsgrupp.Energived);
        }
        return false;
    }

    isNew(): boolean {
        return !(this.handelssortiment && this.handelssortiment.id > 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        const hs: any = changes.handelssortiment;
        if (hs !== undefined && hs.currentValue !== undefined && hs.previousValue !== undefined
            && hs.currentValue !== hs.previousValue) {
            this.handelssortiment.ravaruHandelssortimentId = hs.currentValue.ravaruHandelssortimentId;
            this.setRavaruHandelssortiment();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.sortimentList = this.handelssortimentService.getSortimentLista();
        this.foreslagnamattslagList = this.handelssortimentService.getForseslagnaMattslag();
        this.setRavaruHandelssortiment();
        this.setInitialSelection();

        this.handelssortimentgruppList = this.handelssortimentService.getHandelssortimentgruppLista().pipe(tap(x => this.handelssortimentgruppArray = x));
    }

    removeSortiment() {
        this.choosenSortimentSelection.forEach(s => {
            this.selectedSortiment.splice(this.selectedSortiment.indexOf(s), 1);
            if (s.id === this.handelssortimentSortiment.huvudsortimentId) {
                this.handelssortimentSortiment.huvudsortimentId = undefined;
            }
        });
        this.choosenSortimentSelection = undefined;
        this.updateDto();
    }

    sortimentFormatter = (s: ISortiment) => s ? s.id : undefined;

    updateDto() {
        this.handelssortimentSortiment.sortimentIdLista = this.selectedSortiment.map(c => c.id);
        this.handelssortimentSortimentChange.emit(this.handelssortimentSortiment);
        this.form.markAsDirty();
    }

    viewSortimentFormatter = (s: ISortiment) => s ? `${s.kod} - ${s.namn}` : "Blah";

    private setInitialSelection(): void {
        this.activatedRoute.params.pipe(
            map(params => params.id),
            distinctUntilChanged(),
            switchMap(() => this.sortimentList),
            tap(r => {
                const loadedSelection = r.filter(sl => this.handelssortimentSortiment.sortimentIdLista.includes(sl.id));
                this.selectedSortiment = [...loadedSelection];
                this.initialized = true;
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private setRavaruHandelssortiment(): void {
        if (!this.handelssortiment.ravaruHandelssortimentId) {
            this.ravaruHandelssortiment = null;
        } else {
            this.handelssortimentService.getHandelssortimentById(this.handelssortiment.ravaruHandelssortimentId).subscribe(res => {
                this.ravaruHandelssortiment = res;
            });
        }
    }
}
